import { useQuery } from '@apollo/client';
import previewSrc from '@fanconnect/neo-client/dist/show.html?url';
import { OpenInNew } from '@mui/icons-material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connectionStatus } from '~/components/devices';
import { Link } from '~/components/link';
import { StoppablePreview } from '~/components/preview';
import {
  SettingsBody,
  SettingsData,
  SettingsHeadBar,
  SettingsHeadTitle,
} from '~/components/settings';
import { useAppContext } from '~/contexts';
import { usePreviewControl } from '~/hooks/preview';
import { showPreviewProps } from '~/lib/preview';
import { DevicePreviewDocument } from '../../queries/queries.generated';
import type { Device } from '../types';

interface MiniPreviewProps {
  device: Device;
}

export const MiniPreview = ({ device }: MiniPreviewProps) => {
  const { t, i18n } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const showId = device.activeShow?.show.id;

  const { data: showPreview } = useQuery(DevicePreviewDocument, {
    skip: !showId,
    variables: { networkId: currentNetwork.id, showId: showId as number },
  });

  const previewProps = useMemo(() => {
    const viewId =
      connectionStatus(device, t, i18n.language).status === 'success' && device.currentView?.id
        ? device.currentView.id
        : showPreview?.network?.show?.defaultView.id || '';
    return showPreviewProps(showPreview?.network?.show, viewId, device);
  }, [device, showPreview, t, i18n.language]);

  const previewControl = usePreviewControl('DevicePreview');

  return (
    <>
      <SettingsHeadBar>
        <SettingsHeadTitle>{t('common.labels.preview')}</SettingsHeadTitle>
        <Link
          aria-label={t('common.labels.preview')}
          to="../preview"
          underline="none"
          sx={{ color: 'inherit', display: 'flex' }}
        >
          <OpenInNew />
        </Link>
      </SettingsHeadBar>
      <SettingsBody>
        <SettingsData className="last">
          <StoppablePreview
            key={showPreview?.network?.show?.id || 'no-show'}
            autoplay={currentUser.devicePreviewAutoplay ?? true}
            disableMinHeight
            previewControl={previewControl}
            src={previewSrc}
            {...previewProps}
          />
        </SettingsData>
      </SettingsBody>
    </>
  );
};
